import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { audioTranscribe } from "actions";
import { useDispatch } from "react-redux";

const AudioRecorder = forwardRef(
  (
    {
      setIsRecording,
      containerRef,
      setAudioTranscription,
      setShowRecorder,
      setAudioFile,
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [visible, setVisible] = useState(true);

    const dispatch = useDispatch();

    const canvasRef = useRef(null);
    const analyserRef = useRef(null);
    const audioCtxRef = useRef(null);
    const rafIdRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);

    useImperativeHandle(ref, () => ({
      startRecording,
      stopRecording,
    }));

    const drawBars = () => {
      const canvas = canvasRef.current;
      const analyser = analyserRef.current;
      if (!canvas || !analyser) return;

      const ctx = canvas.getContext("2d");

      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);

      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const centerY = height / 2;

      const bufferLen = analyser.frequencyBinCount;
      const dataArr = new Uint8Array(bufferLen);

      const draw = () => {
        analyser.getByteFrequencyData(dataArr);
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);

        const barW = 2;
        const gap = 4;
        const barCount = Math.floor(width / (barW + gap));

        for (let i = 0; i < barCount; i++) {
          const amp = dataArr[i] ?? 0;
          const minHeight = 2;
          const maxBarHeight = height / 2;
          const dynamicHeight = (amp / 255) * maxBarHeight;
          const barH = Math.max(minHeight, dynamicHeight);
          const x = i * (barW + gap);
          const radius = barW / 2;

          ctx.fillStyle = "#15162D";
          ctx.beginPath();
          ctx.roundRect(x, centerY - barH, barW, barH * 2, radius);
          ctx.fill();
        }

        rafIdRef.current = requestAnimationFrame(draw);
      };

      draw();
    };

    const startRecording = async () => {
      try {
        setVisible(true);
        setError("");

        /*if (containerRef?.current) {
        //containerRef.current.classList.add("hide-editor-main");
      }*/

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream;

        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        const source = audioCtxRef.current.createMediaStreamSource(stream);

        analyserRef.current = audioCtxRef.current.createAnalyser();
        analyserRef.current.fftSize = 1024;
        analyserRef.current.smoothingTimeConstant = 0.8;
        source.connect(analyserRef.current);

        recordedChunksRef.current = [];

        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";

        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.start();

        drawBars();
      } catch (err) {
        setError("Microphone permission denied or recording not supported");
      }
    };

    const stopRecording = () => {
      setAudioTranscription("");

      return new Promise((resolve) => {
        cancelAnimationFrame(rafIdRef.current);

        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.onstop = () => {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            audioCtxRef.current?.close();
            resolve();
          };
          mediaRecorderRef.current.stop();
        } else {
          resolve();
        }
      });
    };

    const cancelRecording = () => {
      stopRecording();
      setVisible(false);
      setIsRecording(false);
      setShowRecorder(false);

      /*if (containerRef?.current) {
      containerRef.current.classList.remove("hide-editor-main");
    }*/

      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    const transcribeAudio = async () => {
      await stopRecording();
      setVisible(false);
      setIsRecording(false);
      setShowRecorder(false);

      /*if (containerRef?.current) {
      containerRef.current.classList.remove("hide-editor-main");
    }*/

      const recordedChunks = recordedChunksRef.current;

      if (!recordedChunks.length) {
        alert("No audio recorded");
        return;
      }

      const audioBlob = new Blob(recordedChunks, { type: "audio/webm" });

      const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, {
        type: "audio/webm",
        lastModified: Date.now(),
      });

      const audioUrl = URL.createObjectURL(audioBlob);

      if (typeof setAudioFile === "function") {
        setAudioFile(audioFile); 
      }
      // Upload to your API
      const formData = new FormData();
      formData.append("audio_files", audioFile);

      const response = await dispatch(audioTranscribe(formData));

      setAudioTranscription(response);
    };

    useEffect(() => () => stopRecording(), []);

    if (!visible) return null;

    return (
      <div className="voice-recorder-section">
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "29px", display: "block" }}
        />
        {error && <p className="text-danger text-center mt-2">{error}</p>}

        <div className="d-flex justify-content-end gap-2 mt-2">
          <button className="border-0 bg-transparent" onClick={cancelRecording}>
            <img src="/assets/images/voice-cancel.svg" alt="Cancel" />
          </button>
          <button onClick={transcribeAudio} className="border-0 bg-transparent">
            <img src="/assets/images/voice-save.svg" alt="Save" />
          </button>
        </div>
      </div>
    );
  }
);

export default AudioRecorder;
